// ---------------------------------------------------------------------------
// C5+C6+C7: Real-Time Collaborative Editing Engine
// ---------------------------------------------------------------------------
// C5: Cursor sharing — colored cursor + name per user, 100ms batched broadcast
// C6: Live edit sync — diffs broadcast via WebSocket, CRDT-like resolution
//     Different components = both apply. Same component = last-write-wins.
// C7: Observer mode + Figma-style comments
//
// Architecture: DO is RELAY only. State lives in DB. Edits are PuckDiffOps
// from Sprint 11, broadcast to all connected users. Each client applies
// incoming diffs to their local state.
// ---------------------------------------------------------------------------

import type { PuckDiffOp } from "../ai/puck-differ.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CollabRole = "editor" | "observer";

export interface CollabUser {
  userId: string;
  displayName: string;
  color: string;
  role: CollabRole;
  /** Cursor position: component ID + relative position within it */
  cursor?: CursorPosition;
  joinedAt: number;
  lastActiveAt: number;
}

export interface CursorPosition {
  componentId: string;
  /** Relative Y position within the component (0-1) */
  relativeY: number;
  /** Viewport scroll position */
  scrollY: number;
}

export interface CollabComment {
  id: string;
  authorId: string;
  authorName: string;
  /** Component this comment is anchored to */
  componentId: string;
  /** Relative position within the component */
  position: { x: number; y: number };
  text: string;
  resolved: boolean;
  resolvedBy?: string;
  resolvedAt?: number;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// WebSocket Message Types
// ---------------------------------------------------------------------------

export const COLLAB_MESSAGE_TYPES = [
  "cursor_update",    // User moved cursor
  "edit_diff",        // User applied a diff to the page
  "user_joined",      // User connected
  "user_left",        // User disconnected
  "comment_added",    // Observer/editor added a comment
  "comment_resolved", // Comment resolved by editor
  "conflict_notice",  // Same component edited by two users
] as const;

export type CollabMessageType = (typeof COLLAB_MESSAGE_TYPES)[number];

export interface CollabMessage {
  type: CollabMessageType;
  userId: string;
  pageId: string;
  timestamp: number;
  payload: Record<string, unknown>;
}

export interface CursorUpdatePayload {
  cursor: CursorPosition;
  displayName: string;
  color: string;
}

export interface UserJoinedPayload {
  user: CollabUser;
}

export interface UserLeftPayload {
  userId: string;
}

export interface EditDiffPayload {
  diff: PageCollabDiff;
  description: string;
  /** Component IDs affected by this diff (for conflict detection) */
  affectedComponentIds: string[];
}

export interface UpdateBlockPropsCollabDiff {
  kind: "update_block_props";
  componentId: string;
  props: Record<string, unknown>;
}

export interface ReplaceDocumentCollabDiff {
  kind: "replace_document";
  data: {
    content?: unknown;
    root?: unknown;
    zones?: unknown;
  };
}

export type PageCollabDiff = PuckDiffOp | UpdateBlockPropsCollabDiff | ReplaceDocumentCollabDiff;

export interface ConflictNoticePayload {
  componentId: string;
  editedBy: string;
  editedByName: string;
  description: string;
}

export interface PageCollabWebsocketClaims {
  version: 1;
  tenantId: string;
  pageId: string;
  userId: string;
  displayName: string;
  color: string;
  role: CollabRole;
  channels: string[];
  issuedAt: number;
  expiresAt: number;
}

export interface PageCollabSessionUser {
  userId: string;
  displayName: string;
  color: string;
  role: CollabRole;
}

export interface PageCollabSessionBridge {
  websocketUrl: string;
  collaborators: CollabUser[];
  currentUser: PageCollabSessionUser;
  expiresAt: string;
}

// ---------------------------------------------------------------------------
// Conflict Detection (C6)
// ---------------------------------------------------------------------------

/**
 * Detect if two diffs conflict (edit the same component).
 * Different components = no conflict (both apply).
 * Same component = conflict (last-write-wins + notification).
 */
export function detectConflict(
  incomingDiff: PuckDiffOp,
  localPendingDiffs: PuckDiffOp[],
): { hasConflict: boolean; conflictingComponentId: string | null } {
  const incomingIds = getAffectedComponentIds(incomingDiff);

  for (const local of localPendingDiffs) {
    const localIds = getAffectedComponentIds(local);
    const overlap = incomingIds.find(id => localIds.includes(id));
    if (overlap) {
      return { hasConflict: true, conflictingComponentId: overlap };
    }
  }

  return { hasConflict: false, conflictingComponentId: null };
}

/**
 * Get component IDs affected by a diff operation.
 */
export function getAffectedComponentIds(diff: PuckDiffOp): string[] {
  switch (diff.action) {
    case "add_component":
      return [diff.component._id ?? diff.component.type];
    case "update_props":
      return [diff.componentId];
    case "remove_component":
      return [diff.componentId];
    case "move_component":
      return [diff.componentId];
    default:
      return [];
  }
}

export function getPageCollabChannel(pageId: string): string {
  return `pages.${pageId}.collab`;
}

export function getPagePresenceChannel(pageId: string): string {
  return `pages.${pageId}.presence`;
}

// ---------------------------------------------------------------------------
// Collab Session State
// ---------------------------------------------------------------------------

export interface CollabSession {
  pageId: string;
  tenantId: string;
  users: Map<string, CollabUser>;
  comments: CollabComment[];
  /** Pending local diffs not yet confirmed by server */
  pendingDiffs: PuckDiffOp[];
}

/**
 * Create a new collaboration session.
 */
export function createCollabSession(pageId: string, tenantId: string): CollabSession {
  return {
    pageId,
    tenantId,
    users: new Map(),
    comments: [],
    pendingDiffs: [],
  };
}

// ---------------------------------------------------------------------------
// User Colors (deterministic from userId)
// ---------------------------------------------------------------------------

const COLLAB_COLORS = [
  "#ef4444", "#f97316", "#eab308", "#22c55e", "#06b6d4",
  "#3b82f6", "#8b5cf6", "#ec4899", "#f43f5e", "#14b8a6",
];

export function getUserColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return COLLAB_COLORS[Math.abs(hash) % COLLAB_COLORS.length]!;
}

// ---------------------------------------------------------------------------
// Session Management
// ---------------------------------------------------------------------------

/**
 * Add or reconnect a user. If userId already exists, overwrites (reconnection).
 * This is intentional — WebSocket reconnections reuse the same userId.
 */
export function addUser(
  session: CollabSession,
  userId: string,
  displayName: string,
  role: CollabRole,
): CollabSession {
  const user: CollabUser = {
    userId,
    displayName,
    color: getUserColor(userId),
    role,
    joinedAt: Date.now(),
    lastActiveAt: Date.now(),
  };
  const users = new Map(session.users);
  users.set(userId, user);
  return { ...session, users };
}

export function removeUser(session: CollabSession, userId: string): CollabSession {
  const users = new Map(session.users);
  users.delete(userId);
  return { ...session, users };
}

export function updateCursor(
  session: CollabSession,
  userId: string,
  cursor: CursorPosition,
): CollabSession {
  const user = session.users.get(userId);
  if (!user) return session;
  const users = new Map(session.users);
  users.set(userId, { ...user, cursor, lastActiveAt: Date.now() });
  return { ...session, users };
}

// ---------------------------------------------------------------------------
// C7: Comments
// ---------------------------------------------------------------------------

export function addComment(
  session: CollabSession,
  comment: Omit<CollabComment, "id" | "resolved" | "createdAt">,
): { session: CollabSession; commentId: string } {
  const id = crypto.randomUUID();
  const newComment: CollabComment = {
    ...comment,
    id,
    resolved: false,
    createdAt: Date.now(),
  };
  return {
    session: { ...session, comments: [...session.comments, newComment] },
    commentId: id,
  };
}

export function resolveComment(
  session: CollabSession,
  commentId: string,
  resolvedBy: string,
): CollabSession {
  return {
    ...session,
    comments: session.comments.map(c =>
      c.id === commentId
        ? { ...c, resolved: true, resolvedBy, resolvedAt: Date.now() }
        : c,
    ),
  };
}

/**
 * Get active (unresolved) comments for the page.
 */
export function getActiveComments(session: CollabSession): CollabComment[] {
  return session.comments.filter(c => !c.resolved);
}

// ---------------------------------------------------------------------------
// Message Builders
// ---------------------------------------------------------------------------

export function buildCursorMessage(
  userId: string,
  pageId: string,
  cursor: CursorPosition,
  displayName: string,
  color: string,
): CollabMessage {
  return {
    type: "cursor_update",
    userId,
    pageId,
    timestamp: Date.now(),
    payload: { cursor, displayName, color } satisfies CursorUpdatePayload,
  };
}

export function buildUserJoinedMessage(
  pageId: string,
  user: CollabUser,
): CollabMessage {
  return {
    type: "user_joined",
    userId: user.userId,
    pageId,
    timestamp: Date.now(),
    payload: { user } satisfies UserJoinedPayload,
  };
}

export function buildUserLeftMessage(
  pageId: string,
  userId: string,
): CollabMessage {
  return {
    type: "user_left",
    userId,
    pageId,
    timestamp: Date.now(),
    payload: { userId } satisfies UserLeftPayload,
  };
}

export function buildEditMessage(
  userId: string,
  pageId: string,
  diff: PuckDiffOp,
  description: string,
): CollabMessage {
  return {
    type: "edit_diff",
    userId,
    pageId,
    timestamp: Date.now(),
    payload: {
      diff,
      description,
      affectedComponentIds: getAffectedComponentIds(diff),
    } satisfies EditDiffPayload,
  };
}

export function buildConflictNotice(
  pageId: string,
  componentId: string,
  editedBy: string,
  editedByName: string,
  description: string,
): CollabMessage {
  return {
    type: "conflict_notice",
    userId: "system",
    pageId,
    timestamp: Date.now(),
    payload: { componentId, editedBy, editedByName, description } satisfies ConflictNoticePayload,
  };
}
